/*
 * Copyright © 2017 - 2019 Atilika Inc. All rights reserved.
 */

import clsx from "clsx";
import {Link} from "gatsby";
import React, {memo} from "react";
import {createUseStyles} from "react-jss";
import ChevronBlue from "../assets/images/icons/chevron-blue.svg";
import ChevronWhite from "../assets/images/icons/chevron-white.svg";
import UI from "./UI";

interface ButtonProps {
    to?: string;
    children: string;
    readMore?: boolean;
    white?: boolean;
    disabled?: boolean;
    type?: "reset" | "button" | "submit";
    onClick?: () => void;
    href?: string;
}

const useStyles = createUseStyles({
    root: {
        backgroundColor: ({white}: ButtonProps) => UI.colors[white ? "white" : "blue"].toCSS(),
        borderRadius: 6,
        border: "1px solid transparent",
        cursor: "pointer",
        color: ({white}: ButtonProps) => UI.colors[white ? "blue" : "white"].toCSS(),
        display: "inline-block",
        fontFamily: UI.fonts.lato,
        fontSize: "1.625rem",
        fontWeight: "normal",
        letterSpacing: ".037rem",
        lineHeight: 2.5,
        padding: "0 1.875rem",
        textAlign: "center",
        textDecoration: "none",
        touchAction: "manipulation",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
        transition: "background .1s ease-in-out, color .1s ease-in-out",
        opacity: ({disabled}: ButtonProps) => (disabled ? 0.5 : undefined),
        "&:hover": {
            textDecoration: "none",
            backgroundColor: ({disabled}: ButtonProps) => {
                return disabled ? UI.colors.blue.toCSS() : UI.colors.blue.darken(0.08).toCSS();
            }
        }
    },
    more: {
        fontSize: ".875rem",
        letterSpacing: ".01875rem",
        lineHeight: "2rem",
        padding: "0 2rem 0 1rem",
        minWidth: 0,
        position: "relative",
        fontWeight: "bold",
        textTransform: "lowercase",
        verticalAlign: "middle",
        "&:hover": {
            transition: "none",
            backgroundColor: ({white}: ButtonProps) =>
                white ? UI.colors.green.toCSS() : undefined,
            color: ({white}: ButtonProps) => (white ? UI.colors.white.toCSS() : undefined),
            "&:after": {
                backgroundImage: ({white}: ButtonProps) =>
                    white ? `url("${ChevronWhite}")` : undefined
            }
        },
        "&:after": {
            content: '""',
            display: "block",
            position: "absolute",
            right: 0,
            backgroundImage: ({white}: ButtonProps) =>
                `url("${white ? ChevronBlue : ChevronWhite}")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "0 50%",
            width: 20,
            height: "2rem",
            top: 0
        }
    }
});

function Button(props: ButtonProps) {
    const classes = useStyles(props);
    const className = clsx(classes.root, {[classes.more]: props.readMore});

    if (props.to) {
        return (
            <Link to={props.to} className={className}>
                {props.children}
            </Link>
        );
    }

    if (props.href) {
        return (
            <a href={props.href} className={className} onClick={props.onClick}>
                {props.children}
            </a>
        );
    }

    return (
        <button
            type={props.type}
            className={className}
            onClick={props.onClick}
            disabled={props.disabled}
        >
            {props.children}
        </button>
    );
}

export default memo(Button);
